/**
 * MaterialShortageAlert Component
 *
 * Alert banner showing material shortages for upcoming production.
 * Fetches data from production_material_requirements view.
 * Displays in Production Timeline when shortfalls exist.
 *
 * Part of US-P2-010: Process Recipe Integration
 */

import { useState } from 'react'
import { useMaterialRequirements } from '@/hooks/useMaterialRequirements'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Package,
  ShoppingCart
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseDateString } from '@/utils/dateUtils'

/**
 * Format a number with appropriate decimal places
 * @param {number} value - The number to format
 * @param {number} decimals - Decimal places (default 2)
 * @returns {string} Formatted number
 */
function formatQuantity(value, decimals = 2) {
  if (value === null || value === undefined) return '-'
  const num = Number(value)
  if (Number.isInteger(num)) return num.toString()
  return num.toFixed(decimals)
}

/**
 * Format a date string to short format
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-'
  return parseDateString(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })
}

/**
 * Severity level based on shortfall magnitude
 * @param {number} required - Required quantity
 * @param {number} shortfall - Shortfall amount
 * @returns {'critical' | 'warning' | 'info'}
 */
function getSeverity(required, shortfall) {
  if (!required || !shortfall) return 'info'
  const percentage = (shortfall / required) * 100
  if (percentage >= 50) return 'critical'
  if (percentage >= 25) return 'warning'
  return 'info'
}

/**
 * Badge variant based on severity
 * @param {'critical' | 'warning' | 'info'} severity
 * @returns {string} Badge variant
 */
function getSeverityVariant(severity) {
  switch (severity) {
    case 'critical':
      return 'destructive'
    case 'warning':
      return 'warning'
    default:
      return 'secondary'
  }
}

/**
 * MaterialShortageAlert Component
 *
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.compact - Show compact version (just count badge)
 * @param {Function} props.onOrderMaterials - Callback when "Order Materials" clicked
 */
export default function MaterialShortageAlert({
  className,
  compact = false,
  onOrderMaterials
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Fetch material requirements with shortfalls only
  const {
    consolidatedShortfalls,
    byBatch,
    summary,
    loading,
    error
  } = useMaterialRequirements({ shortfallsOnly: true })

  // Don't render if no shortfalls
  if (!loading && (!consolidatedShortfalls || consolidatedShortfalls.length === 0)) {
    return null
  }

  // Loading state
  if (loading) {
    return (
      <Alert className={cn('animate-pulse', className)}>
        <Package className="h-4 w-4" />
        <AlertTitle>Checking material requirements...</AlertTitle>
      </Alert>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error loading material requirements</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  // Get batches with critical shortfalls
  const criticalBatches = byBatch.filter(b => b.has_critical_shortfall)
  const hasCritical = criticalBatches.length > 0

  // Compact mode - just show badge
  if (compact) {
    return (
      <Badge
        variant={hasCritical ? 'destructive' : 'warning'}
        className={cn('cursor-pointer', className)}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <AlertTriangle className="h-3 w-3 mr-1" />
        {consolidatedShortfalls.length} material{consolidatedShortfalls.length !== 1 ? 's' : ''} low
      </Badge>
    )
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={className}
    >
      <Alert
        variant={hasCritical ? 'destructive' : 'warning'}
        className={cn(
          'transition-all',
          hasCritical ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50'
        )}
      >
        <div className="flex items-start gap-3 w-full">
          <AlertTriangle className={cn(
            'h-5 w-5 mt-0.5',
            hasCritical ? 'text-red-600' : 'text-amber-600'
          )} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <AlertTitle className={cn(
                'font-semibold',
                hasCritical ? 'text-red-800' : 'text-amber-800'
              )}>
                Material Shortages Detected
              </AlertTitle>

              <div className="flex items-center gap-2">
                {/* Summary badges */}
                <Badge variant="outline" className="whitespace-nowrap">
                  {summary.items_with_shortfall} item{summary.items_with_shortfall !== 1 ? 's' : ''}
                </Badge>
                <Badge variant="outline" className="whitespace-nowrap">
                  {summary.batches_with_shortfall} batch{summary.batches_with_shortfall !== 1 ? 'es' : ''}
                </Badge>

                {/* Expand button */}
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>

            <AlertDescription className={cn(
              'mt-1',
              hasCritical ? 'text-red-700' : 'text-amber-700'
            )}>
              {hasCritical
                ? `${criticalBatches.length} batch${criticalBatches.length !== 1 ? 'es' : ''} cannot start without additional materials`
                : 'Some upcoming batches have material requirements that exceed current stock'
              }
            </AlertDescription>
          </div>
        </div>

        {/* Expanded content */}
        <CollapsibleContent className="mt-4">
          <div className="space-y-4">
            {/* Materials table */}
            <div className="border rounded-lg overflow-hidden bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Required</TableHead>
                    <TableHead className="text-right">In Stock</TableHead>
                    <TableHead className="text-right">Shortfall</TableHead>
                    <TableHead>Batches Affected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consolidatedShortfalls.map(item => {
                    const severity = getSeverity(item.total_quantity_required, item.total_shortfall)
                    return (
                      <TableRow key={item.inventory_item_id}>
                        <TableCell className="font-medium">
                          {item.item_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {item.item_category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatQuantity(item.total_quantity_required)} {item.unit}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatQuantity(item.current_stock)} {item.unit}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={getSeverityVariant(severity)}>
                            -{formatQuantity(item.total_shortfall)} {item.unit}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.batches.slice(0, 3).map(b => (
                              <Badge
                                key={b.batch_id}
                                variant="outline"
                                className="font-mono text-xs"
                              >
                                {b.batch_code}
                              </Badge>
                            ))}
                            {item.batches.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{item.batches.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Affected batches summary */}
            {criticalBatches.length > 0 && (
              <div className="p-3 bg-red-100 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="font-medium text-red-800">
                    Batches that cannot start:
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {criticalBatches.map(b => (
                    <div
                      key={b.batch_id}
                      className="inline-flex items-center gap-2 bg-white px-2 py-1 rounded border border-red-200"
                    >
                      <span className="font-mono text-sm">{b.batch_code}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(b.planned_start_date)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              {onOrderMaterials && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOrderMaterials}
                  className="gap-2"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Order Materials
                </Button>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Alert>
    </Collapsible>
  )
}

/**
 * Inline material warning for use in dialogs
 * Shows a compact warning when materials are insufficient for a specific batch
 */
export function BatchMaterialWarning({
  batchId,
  className,
  onProceedAnyway
}) {
  const { isAvailable, missingItems, loading } = useMaterialRequirements({
    batchId,
    shortfallsOnly: false
  })

  // Don't show if materials are available or still loading
  if (loading || isAvailable) {
    return null
  }

  if (missingItems.length === 0) {
    return null
  }

  return (
    <Alert variant="warning" className={cn('border-amber-500 bg-amber-50', className)}>
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-800">Insufficient Materials</AlertTitle>
      <AlertDescription className="text-amber-700">
        <p className="mb-2">
          This batch requires materials that are not fully available:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm">
          {missingItems.map(item => (
            <li key={item.inventory_item_id}>
              <span className="font-medium">{item.item_name}</span>
              : need {formatQuantity(item.quantity_required)} {item.unit},
              have {formatQuantity(item.current_stock)} {item.unit}
              <span className="text-red-600 ml-1">
                (short {formatQuantity(item.shortfall)})
              </span>
            </li>
          ))}
        </ul>
        {onProceedAnyway && (
          <div className="mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onProceedAnyway}
              className="text-amber-700 border-amber-400 hover:bg-amber-100"
            >
              Proceed Anyway
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  )
}
